import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CustomerOrderStatus, OrderDTO } from '@scan2eat/shared';
import { formatMoney } from '@scan2eat/shared';
import { SESSION_TOKEN_STORAGE_KEY } from '@scan2eat/shared';
import { apiFetch, apiFetchBlob } from '../api/client.js';
import { downloadBlob } from '../lib/download.js';
import { useRealtimeSync } from '../realtime/useRealtimeSync.js';
import { useCartStore } from '../store/cart.js';
import { labels } from '../labels.js';
import { BottomNav } from '../components/BottomNav.js';
import { ClientHeader } from '../components/ClientHeader.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import styles from './OrdersPage.module.css';

/** Badge variant per customer status: .badge--* classes in styles/global.css. */
export const CUSTOMER_BADGE: Record<CustomerOrderStatus, string> = {
  PENDING: 'pending', PREPARING: 'preparing', READY: 'ready', SERVED: 'served', CANCELLED: 'cancelled',
};

export function OrdersPage() {
  const [orders, setOrders] = useState<OrderDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [sessionClosed, setSessionClosed] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const token = localStorage.getItem(SESSION_TOKEN_STORAGE_KEY);

  const load = useCallback(() => {
    if (!token) return;
    return apiFetch<{ orders: OrderDTO[] }>('/public/orders')
      .then((d) => { setOrders(d.orders); setLoadError(false); })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const { socket } = useRealtimeSync(token, load);

  useEffect(() => {
    if (!socket) return;
    const onStatus = (e: { payload: { orderId: string } }) => {
      apiFetch<OrderDTO>(`/public/orders/${e.payload.orderId}`).then((o) => {
        setOrders((prev) => prev.map((x) => (x.id === o.id ? o : x)));
      }).catch(() => { /* noop */ });
    };
    const onCreated = (e: { payload: { order: OrderDTO } }) => {
      setOrders((prev) => (prev.some((x) => x.id === e.payload.order.id) ? prev : [e.payload.order, ...prev]));
    };
    // Une cloture par paiement laisse la liste consultable : le recu de chaque
    // commande reste telechargeable jusqu'a expiresAt. Voir docs/07, section 2.4.
    const onClosed = (e: { payload: { reason: string } }) => {
      if (e.payload.reason === 'PAYMENT_COMPLETED') {
        load();
        return;
      }
      setSessionClosed(true);
    };
    socket.on('order:status_changed' as never, onStatus as never);
    socket.on('order:created' as never, onCreated as never);
    socket.on('session:closed' as never, onClosed as never);
    return () => {
      socket.off('order:status_changed' as never, onStatus as never);
      socket.off('order:created' as never, onCreated as never);
      socket.off('session:closed' as never, onClosed as never);
    };
  }, [socket, load]);

  const downloadReceipt = async (order: OrderDTO) => {
    setDownloadError(null);
    try {
      const blob = await apiFetchBlob(`/public/orders/${order.id}/invoice.pdf`);
      downloadBlob(blob, `recu-${order.number}.pdf`);
    } catch {
      setDownloadError(labels.errors.generic);
    }
  };

  if (sessionClosed) {
    return (
      <main className={`client-screen ${styles.centered}`}>
        <EmptyState icon="scan" title={labels.errors.sessionClosed} text={labels.errors.thanks}>
          <button onClick={() => { localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY); useCartStore.getState().clear(); window.location.href = '/scan'; }} className="btn-primary btn-lg">
            <Icon name="scan" size={20} />
            {labels.app.rescan}
          </button>
        </EmptyState>
      </main>
    );
  }

  const time = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className={`client-screen ${styles.screen}`}>
      <ClientHeader title={labels.order.allOrders} backTo="/menu" backLabel={labels.menu.title} />

      <main className={styles.main}>
        {downloadError && (
          <p role="alert" className="notice notice--danger">
            <Icon name="alert" size={18} />
            {downloadError}
          </p>
        )}

        {loading ? (
          <div className={styles.list}>
            {Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton" style={{ height: '6.5rem', borderRadius: 'var(--radius-lg)' }} />)}
          </div>
        ) : loadError ? (
          <EmptyState icon="wifi-off" text={labels.errors.network} role="alert">
            <button onClick={load} className="btn-primary">
              <Icon name="refresh" size={18} />
              {labels.errors.retry}
            </button>
          </EmptyState>
        ) : orders.length === 0 ? (
          <EmptyState icon="receipt" title={labels.order.noOrdersTitle} text={labels.order.noOrders}>
            <Link to="/menu" className="btn-primary btn-lg">
              <Icon name="menu" size={20} />
              {labels.cart.browse}
            </Link>
          </EmptyState>
        ) : (
          <ul className={styles.list}>
            {orders.map((o) => (
              <li key={o.id} className={styles.card}>
                <Link to={`/commandes/${o.id}`} className={styles.link}>
                  <span className={styles.head}>
                    <span className={styles.number}>{labels.order.number(o.number)}</span>
                    <span className={`badge badge--dot badge--${CUSTOMER_BADGE[o.customerStatus]}`}>
                      {labels.customerStatus[o.customerStatus]}
                    </span>
                  </span>
                  <span className={styles.meta}>
                    {labels.order.placedAt(time(o.placedAt))} · {labels.menu.itemCount(o.itemCount)}
                  </span>
                  <span className={styles.foot}>
                    <span className={styles.total}>{formatMoney(o.totalMinor, o.currency)}</span>
                    <span className={styles.follow}>
                      {labels.order.follow}
                      <Icon name="chevron-right" size={18} />
                    </span>
                  </span>
                </Link>

                {/* Le recu de chaque commande payee, y compris celles des
                    sessions precedentes : payer ferme la session, et le
                    convive doit garder ses factures. Voir docs/07, section 2.3. */}
                {o.hasInvoice && (
                  <button type="button" onClick={() => void downloadReceipt(o)} className={styles.receipt}>
                    <Icon name="download" size={18} />
                    {labels.order.downloadReceipt}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
